import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, X, Info } from "lucide-react";
import wheelImg from "@/assets/color-neutralizing-wheel.webp";

type Tone = "yellow" | "orange" | "red" | "violet" | "blue" | "green" | null;

interface Props {
  /** Free text we'll scan for pull-tone keywords (formula, brief, etc.) */
  context?: string;
  /** Force-highlight a specific tone */
  highlight?: Tone;
  /** Compact mode hides the legend by default */
  compact?: boolean;
  title?: string;
}

const NEUTRALIZER: Record<Exclude<Tone, null>, Exclude<Tone, null>> = {
  yellow: "violet",
  orange: "blue",
  red: "green",
  violet: "yellow",
  blue: "orange",
  green: "red",
};

const LABEL: Record<Exclude<Tone, null>, string> = {
  yellow: "Yellow (L9–10)",
  orange: "Orange (L6–8)",
  red: "Red (L4–5)",
  violet: "Violet (L2–3)",
  blue: "Blue (L1)",
  green: "Green",
};

function detectPull(text?: string): Tone {
  if (!text) return null;
  const t = text.toLowerCase();
  if (/\b(brassy|orange|copper(y)?)\b/.test(t)) return "orange";
  if (/\b(yellow|gold(y)?|banana)\b/.test(t)) return "yellow";
  if (/\b(red|pink|rosy|warm(th)?\s*pull)\b/.test(t)) return "red";
  if (/\b(green|seaweed|swampy|ash(y)?\s*green)\b/.test(t)) return "green";
  if (/\b(violet|purple)\s*(pull|cast|tone)?\b/.test(t)) return "violet";
  if (/\b(blue|smoky|steel)\s*(pull|cast|tone)?\b/.test(t)) return "blue";
  return null;
}

export function ColorNeutralizingChart({
  context,
  highlight,
  compact = false,
  title = "Color Brain — Neutralizing Wheel",
}: Props) {
  const [open, setOpen] = useState(false);
  const detected = useMemo<Tone>(
    () => highlight ?? detectPull(context),
    [highlight, context]
  );
  const neutralizer = detected ? NEUTRALIZER[detected] : null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-xl border border-primary/40 bg-card p-4 shadow-[0_0_24px_-8px_hsl(var(--primary)/0.55)]"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base text-foreground">{title}</h3>
            {detected && neutralizer ? (
              <p className="mt-1 font-body text-sm text-foreground/85">
                Pulling{" "}
                <span className="font-semibold text-foreground">
                  {LABEL[detected]}
                </span>{" "}
                → neutralize with{" "}
                <span className="font-semibold text-primary">
                  {LABEL[neutralizer]}
                </span>
                .
              </p>
            ) : (
              <p className="mt-1 font-body text-xs text-muted-foreground">
                Tap a tone to see what cancels it. Opposites neutralize.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Expand chart"
            className="rounded-md border border-border p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block w-full overflow-hidden rounded-lg bg-black"
        >
          <img
            src={wheelImg}
            alt="Primary and secondary color neutralizing wheel — yellow, orange, red, violet, blue, green with hair level numbers"
            className="mx-auto block max-h-[260px] w-auto object-contain"
            loading="lazy"
          />
        </button>

        {!compact && (
          <div className="mt-3 grid grid-cols-3 gap-1.5 text-[11px] sm:text-xs">
            {(Object.keys(NEUTRALIZER) as Exclude<Tone, null>[]).map((t) => {
              const isHit = detected === t || neutralizer === t;
              return (
                <div
                  key={t}
                  className={`rounded-md border px-2 py-1.5 text-center font-body transition ${
                    isHit
                      ? "border-primary bg-primary/15 text-foreground"
                      : "border-border/60 bg-muted/30 text-muted-foreground"
                  }`}
                >
                  <div className="font-semibold capitalize">{t}</div>
                  <div className="opacity-75">cancels {NEUTRALIZER[t]}</div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-3 flex items-start gap-1.5 font-body text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
          Numbers = level system. Lower numbers (1–3) = darker underlying pigment
          (blue/violet). Higher numbers (8–10) = lighter underlying pigment
          (orange/yellow).
        </p>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setOpen(false)}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full border border-white/30 bg-black/40 p-2 text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={wheelImg}
              alt="Color neutralizing wheel — full view"
              className="max-h-[90vh] max-w-full object-contain"
              loading="lazy"
              decoding="async"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ColorNeutralizingChart;
